import { useState, useRef } from 'react'
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Loader2, Plus, Printer, Play, CheckCircle2, Clock, Package2, X, Receipt, FileText, ChevronDown, Activity,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { ModalFaturar, ModalProgressoFaturamento } from './CargasDistribuicao'

interface Carga {
  id: number
  numero: number
  nome: string
  status: string
  tipo?: string
  modo_separacao: string
  total_pedidos: number
  veiculo_id?: number | null
  motorista_id?: number | null
  criado_em: string | null
  iniciada_em: string | null
  concluida_em: string | null
}

const STATUS_META: Record<string, { label: string; cor: string; icon: any }> = {
  em_separacao: { label: 'Em separação', cor: 'bg-indigo-100 text-indigo-800', icon: Play },
  concluida: { label: 'Concluída', cor: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  aberta: { label: 'Aberta', cor: 'bg-amber-100 text-amber-800', icon: Clock },
  faturada: { label: 'Faturada', cor: 'bg-cyan-100 text-cyan-800', icon: Receipt },
  em_rota: { label: 'Em rota', cor: 'bg-blue-100 text-blue-800', icon: Play },
  em_acerto: { label: 'Em acerto', cor: 'bg-violet-100 text-violet-800', icon: Clock },
  fechada: { label: 'Fechada', cor: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  cancelada: { label: 'Cancelada', cor: 'bg-red-100 text-red-800', icon: X },
}
// ordem de ciclo de vida pros chips de filtro
const STATUS_ORDER = ['aberta', 'em_separacao', 'concluida', 'faturada', 'em_rota', 'em_acerto', 'fechada', 'cancelada']
// "Ativos" = em andamento (exclui as finalizadas/canceladas) — é o filtro padrão
const STATUS_ATIVOS = ['aberta', 'em_separacao', 'concluida', 'faturada', 'em_rota', 'em_acerto']

export default function OndasPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const { data: cargas = [], isLoading } = useQuery<Carga[]>({
    queryKey: ['cargas'],
    queryFn: async () => (await api.get('/vendas/cargas/')).data,
    refetchOnWindowFocus: true,
  })

  // Filtro por status — começa mostrando só os ATIVOS (esconde fechada/cancelada)
  const [filtroStatus, setFiltroStatus] = useState<Set<string>>(new Set(STATUS_ATIVOS))
  const contagem = cargas.reduce((acc: Record<string, number>, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1; return acc
  }, {})
  const cargasFiltradas = cargas.filter(c => filtroStatus.has(c.status))
  const toggleStatus = (s: string) => setFiltroStatus(prev => {
    const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n
  })

  const [faturarCarga, setFaturarCarga] = useState<any>(null)
  const [monitorCargaId, setMonitorCargaId] = useState<number | null>(null)
  const faturar = useMutation({
    mutationFn: async ({ id, sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }: { id: number; sem_nota_ids: number[]; motorista_id: number | null; veiculo_id: number | null; conta_bancaria_id?: number | null }) =>
      (await api.post(`/vendas/cargas/${id}/faturar`, { sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }, { timeout: 60000 })).data,
    onSuccess: (d: any, vars: any) => {
      toast.success(`Faturamento iniciado — acompanhe o progresso (${d.total} pedidos)`, { duration: 4000 })
      setFaturarCarga(null)
      setMonitorCargaId(vars.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao faturar carga'),
  })

  const cancelarMut = useMutation({
    mutationFn: async (cargaId: number) =>
      (await api.post(`/vendas/cargas/${cargaId}/cancelar`)).data,
    onSuccess: (r: any) => {
      const rev = (r?.pedidos_revertidos || []).length
      const sol = (r?.pedidos_soltos || []).length
      toast.success(`Onda cancelada — ${rev} pedido(s) voltaram para 'aprovado'${sol > 0 ? `, ${sol} já avançado(s) só desvinculado(s)` : ''}`)
      qc.invalidateQueries({ queryKey: ['cargas'] })
      qc.invalidateQueries({ queryKey: ['pedidos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })

  async function editarNomeCarga(c: Carga) {
    const novo = window.prompt(`Nome da carga #${c.numero} (rota/identificação):`, c.nome || '')
    if (novo === null) return
    try {
      await api.patch(`/vendas/cargas/${c.id}`, { nome: novo.trim() })
      toast.success('Nome atualizado')
      qc.invalidateQueries({ queryKey: ['cargas'] })
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao salvar') }
  }

  function cancelarOnda(c: Carga) {
    const msg = `Cancelar onda #${c.numero}?\n\n` +
      `Os pedidos em separação voltam para "aprovado".\n` +
      `Pedidos que já avançaram (entregue/expedido) só perdem o vínculo com a onda.`
    if (!window.confirm(msg)) return
    cancelarMut.mutate(c.id)
  }

  function abrirPdfCarga(id: number, rota: string, erro: string) {
    const token = localStorage.getItem('pain_token')
    const t = toast.loading('Gerando PDF...')
    fetch(`/api/vendas/cargas/${id}/${rota}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'Nenhuma NF/pedido encontrado' : `HTTP ${r.status}`); return r.blob() })
      .then(blob => {
        toast.dismiss(t)
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      })
      .catch(e => { toast.dismiss(t); toast.error(`${erro}: ${e.message}`) })
  }
  const reimprimir = (id: number) => abrirPdfCarga(id, 'mapa-pdf', 'Falha no mapa')

  function formatarData(iso: string | null): string {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString('pt-BR') } catch { return iso }
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/vendas')} className="p-2 rounded hover:bg-ink-100">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink-900">Ondas de separação</h1>
            <p className="text-sm text-ink-500">Cargas consolidadas — reimprimir mapa ou continuar bipagem.</p>
          </div>
        </div>
        <button onClick={() => nav('/vendas/separacao')} className="btn-primary">
          <Plus size={14} /> Nova onda
        </button>
      </div>

      {/* Filtro por status (padrão: só ativos) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-ink-500 mr-1">Status:</span>
        <button onClick={() => setFiltroStatus(new Set(STATUS_ATIVOS))}
          className="text-xs px-2.5 py-1 rounded-full border border-ink-200 bg-white text-ink-600 hover:border-ink-300">
          Ativos
        </button>
        <button onClick={() => setFiltroStatus(new Set(STATUS_ORDER))}
          className="text-xs px-2.5 py-1 rounded-full border border-ink-200 bg-white text-ink-600 hover:border-ink-300">
          Todos
        </button>
        <span className="w-px h-4 bg-ink-200 mx-1" />
        {STATUS_ORDER.map(s => {
          const meta = STATUS_META[s]
          const on = filtroStatus.has(s)
          const n = contagem[s] || 0
          return (
            <button key={s} onClick={() => toggleStatus(s)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? meta.cor + ' border-transparent font-medium' : 'bg-white border-ink-200 text-ink-400 hover:border-ink-300'}`}>
              {meta.label}{n > 0 && <span className="ml-1 opacity-70">({n})</span>}
            </button>
          )
        })}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-ink-400">
            <Loader2 className="animate-spin inline mr-2" /> Carregando...
          </div>
        ) : cargasFiltradas.length === 0 ? (
          <div className="p-10 text-center text-ink-400 text-sm">
            <Package2 size={32} className="mx-auto mb-2 opacity-40" />
            {cargas.length === 0
              ? <>Nenhuma onda criada. Clique em <strong>Nova onda</strong> para começar.</>
              : <>Nenhuma onda nos status filtrados. Clique em <strong>Todos</strong> para ver as finalizadas/canceladas.</>}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Onda</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nome</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Modo</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Pedidos</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Iniciada</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {cargasFiltradas.map(c => {
                const meta = STATUS_META[c.status] || { label: c.status, cor: 'bg-gray-100 text-gray-700', icon: Clock }
                const Icon = meta.icon
                return (
                  <tr key={c.id} className="hover:bg-ink-50">
                    <td className="px-3 py-2.5 font-semibold text-sm">
                      <span className="text-indigo-600">🌊 #{c.numero}</span>
                    </td>
                    <td className="px-3 py-2.5 text-sm">
                      <button onClick={() => editarNomeCarga(c)}
                        className="text-left hover:text-brand-700 hover:underline"
                        title="Clique pra editar o nome">
                        {c.nome || <span className="text-ink-400 italic">— editar —</span>}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-ink-600 capitalize">{c.modo_separacao}</td>
                    <td className="px-3 py-2.5 text-center text-sm">{c.total_pedidos}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${meta.cor}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-ink-600">{formatarData(c.iniciada_em)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === 'em_separacao' && (
                          <button onClick={() => nav(`/vendas/cargas/${c.id}/separacao`)}
                            className="text-xs px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1">
                            <Play size={10} /> Continuar bipagem
                          </button>
                        )}
                        {c.tipo === 'distribuicao' && c.status === 'aberta' && (
                          <button onClick={() => setFaturarCarga(c)} disabled={faturar.isPending}
                            className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center gap-1">
                            <Receipt size={10} /> Faturar
                          </button>
                        )}
                        {c.tipo === 'distribuicao' && ['aberta', 'faturada'].includes(c.status) && (
                          <button onClick={() => setMonitorCargaId(c.id)} title="Acompanhar faturamento pedido-a-pedido"
                            className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center gap-1">
                            <Activity size={10} /> Monitor
                          </button>
                        )}
                        {['em_separacao', 'aberta'].includes(c.status) && (
                          <button onClick={() => cancelarOnda(c)}
                            disabled={cancelarMut.isPending}
                            className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white flex items-center gap-1">
                            <X size={10} /> Cancelar
                          </button>
                        )}
                        <ImpressoesMenu cargaId={c.id} abrir={abrirPdfCarga} />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {faturarCarga && <ModalFaturar carga={faturarCarga} faturando={faturar.isPending}
        onClose={() => setFaturarCarga(null)}
        onFaturar={(semNotaIds: number[], motoristaId: number | null, veiculoId: number | null, bancoBoletoId: number | null) =>
          faturar.mutate({ id: faturarCarga.id, sem_nota_ids: semNotaIds, motorista_id: motoristaId, veiculo_id: veiculoId, conta_bancaria_id: bancoBoletoId })} />}
      {monitorCargaId != null && <ModalProgressoFaturamento cargaId={monitorCargaId}
        onClose={() => { setMonitorCargaId(null); qc.invalidateQueries({ queryKey: ['cargas'] }) }} />}
    </div>
  )
}


// Menu único "Impressões" da carga (mapa de separação, pedidos em lote, notas em lote)
function ImpressoesMenu({ cargaId, abrir }: { cargaId: number; abrir: (id: number, rota: string, erro: string) => void }) {
  // menu posicionado FIXED (ancorado no botão) — a tabela tem overflow-hidden e cortaria
  // um dropdown absolute. Calcula a posição pelo rect do botão ao abrir.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const itens: [string, string, string][] = [
    ['Imprimir tudo', 'imprimir-tudo-pdf', 'Falha ao montar impressão completa'],
    ['Mapa de separação', 'mapa-pdf', 'Falha no mapa'],
    ['Pedidos em lote', 'pedidos-pdf', 'Falha nos pedidos'],
    ['Pré-visualizar notas (impostos)', 'preview-notas-pdf', 'Falha no preview'],
    ['Pré-visualizar boletos', 'boletos-preview-pdf', 'Falha na prévia de boletos'],
    ['Notas em lote (DANFE)', 'notas-pdf', 'Falha nas notas'],
  ]
  const toggle = () => {
    if (pos) { setPos(null); return }
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 200) })
  }
  return (
    <div className="inline-block">
      <button ref={btnRef} onClick={toggle}
        className="text-xs px-2 py-1 rounded bg-slate-600 hover:bg-slate-700 text-white flex items-center gap-1">
        <Printer size={10} /> Impressões <ChevronDown size={10} />
      </button>
      {pos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPos(null)} />
          <div className="fixed z-50 bg-white border border-ink-200 rounded-lg shadow-lg py-1 w-[200px] text-left"
            style={{ top: pos.top, left: pos.left }}>
            {itens.map(([label, rota, erro]) => (
              <button key={rota}
                onClick={() => { setPos(null); abrir(cargaId, rota, erro) }}
                className="w-full text-left px-3 py-2 text-xs text-ink-700 hover:bg-slate-50 flex items-center gap-2">
                <FileText size={12} className="text-slate-500" /> {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
